import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";

export type QueryModalOptions = {
    /** query key used to flag create mode, default: "new" */
    newKey?: string;
    /** query key used to hold edit id, default: "edit" */
    editKey?: string;
    /** when closing, use history.replace instead of push, default: true */
    replaceOnClose?: boolean;
};

export function useQueryModal(opts: QueryModalOptions = {}) {
    const {
        newKey = "new",
        editKey = "edit",
        replaceOnClose = true,
    } = opts;

    const [searchParams, setSearchParams] = useSearchParams();

    const setParam = useCallback(
        (mut: (p: URLSearchParams) => void, options?: { replace?: boolean }) => {
            setSearchParams(prev => {
                const p = new URLSearchParams(prev);
                mut(p);
                return p;
            }, options);
        },
        [setSearchParams]
    );

    const openCreate = useCallback(() => {
        setParam(p => {
            p.delete(editKey);
            p.set(newKey, "1");
        });
    }, [setParam, editKey, newKey]);

    const openEdit = useCallback((id: string) => {
        setParam(p => {
            p.delete(newKey);
            p.set(editKey, id);
        });
    }, [setParam, newKey, editKey]);

    const closeModal = useCallback(() => {
        setParam(
            p => {
                p.delete(newKey);
                p.delete(editKey);
            },
            {replace: replaceOnClose}
        );
    }, [setParam, newKey, editKey, replaceOnClose]);

    const state = useMemo(() => {
        const editId = searchParams.get(editKey);
        const isNew = searchParams.get(newKey) === "1";
        const modalOpen = Boolean(isNew || editId);
        return {editId, isNew, modalOpen};
    }, [searchParams, editKey, newKey]);

    return {
        searchParams,
        ...state,
        setParam,
        openCreate,
        openEdit,
        closeModal,
    };
}
